import { Injectable } from '@angular/core';
import { ProjectResponseGetById } from '@customer-apps/api-planning-projects';
import {
    ConsumptionProfile,
    FormKeys,
    HeatPumpOperatingMode,
    HeatingDistributionMethod,
    HeatingSystemType,
    ProductType,
    ProjectType
} from '@customer-apps/shared/enums';
import {
    EnergyConsumptionFormValue,
    HeatGeneratorDetails,
    HeatPumpIntermediaryState,
    HeatingDistribution,
    HouseholdValues
} from '@customer-apps/shared/interfaces';
import { LodashService, SnackbarService } from '@customer-apps/shared/services';
import { hasAllPropsDefined } from '@customer-apps/shared/utils';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediaryPrefillService {
    constructor(private translateService: TranslateService, private snackbarService: SnackbarService) {}

    public projectByLivingAreaOrCustomValueToState(project: ProjectResponseGetById): HeatPumpIntermediaryState {
        return {
            building: this.projectToBuildingState(project),
            isOldDeviceUsed: this.projectToDeviceState(project),
            energyConsumption: this.projectToEnergyConsumptionState(project),
            heatGeneratorDetails: this.projectToHeatGeneratorDetailsState(project),
            heatingDistribution: this.projectToHeatingDistributionState(project),
            household: this.projectToHouseholdState(project)
        };
    }

    public projectByConsumptionToState(project: ProjectResponseGetById): HeatPumpIntermediaryState {
        return {
            building: this.projectToBuildingState(project),
            isOldDeviceUsed: this.projectToDeviceState(project),
            household: this.projectToHouseholdState(project)
        };
    }

    public projectByBuildingHullToState(project: ProjectResponseGetById): HeatPumpIntermediaryState {
        return {
            isOldDeviceUsed: this.projectToDeviceState(project),
            energyConsumption: this.projectToEnergyConsumptionState(project),
            heatGeneratorDetails: this.projectToHeatGeneratorDetailsState(project),
            heatingDistribution: this.projectToHeatingDistributionState(project),
            household: this.projectToHouseholdState(project)
        };
    }

    public projectByNewConstructionToState(project: ProjectResponseGetById): HeatPumpIntermediaryState {
        return {
            energyConsumption: this.projectToEnergyConsumptionState(project),
            heatingDistribution: this.projectToHeatingDistributionState(project),
            household: this.projectToHouseholdState(project)
        };
    }

    private projectToHeatGeneratorDetailsState(project: ProjectResponseGetById) {
        try {
            const currentHeatingSystem = project.building.currentHeatingSystem!;
            const heatGeneratorDetailsState: HeatGeneratorDetails = {
                boilerType: currentHeatingSystem.heatingSystemType as HeatingSystemType,
                gasLossExhaustKnown: !!currentHeatingSystem.exhaustGasLosses,
                installationYear: currentHeatingSystem.installationConstructionYear!.toString(),
                nominalPower: currentHeatingSystem.heatGeneratorPower!
            };

            if (!LodashService.isNil(currentHeatingSystem.isLowTemperature)) {
                heatGeneratorDetailsState.lowTemperature = currentHeatingSystem.isLowTemperature;
            }

            if (!LodashService.isNil(currentHeatingSystem.exhaustGasLosses)) {
                heatGeneratorDetailsState.gasLossExhaust = currentHeatingSystem.exhaustGasLosses;
            }

            if (currentHeatingSystem.heatPumpOperatingMode) {
                heatGeneratorDetailsState.heatPumpOperatingMode = currentHeatingSystem.heatPumpOperatingMode as HeatPumpOperatingMode;
            }

            if (!hasAllPropsDefined(heatGeneratorDetailsState)) {
                this.showError();
                throw Error('Project does not contain all fields required for recalculation');
            }
            return heatGeneratorDetailsState;
        } catch (error) {
            this.showError();
            throw Error(`Project data is corrupt. Cannot prefill the state. ${error}`);
        }
    }

    private projectToBuildingState(project: ProjectResponseGetById) {
        try {
            const buildingState = { [FormKeys.BuildingConstructionYear]: project!.building.buildingConstructionYear!.toString() };
            if (!hasAllPropsDefined(buildingState)) {
                this.showError();
                throw Error('Project does not contain all fields required for recalculation');
            }
            return buildingState;
        } catch (error) {
            this.showError();
            throw Error(`Project data is corrupt. Cannot prefill the state. ${error}`);
        }
    }

    private projectToDeviceState(project: ProjectResponseGetById): { value: boolean } {
        try {
            const deviceState = { value: project!.building.currentHeatingSystem!.currentHeaterAsPeakLoadDevice! };
            if (!hasAllPropsDefined(deviceState)) {
                this.showError();
                throw Error('Project does not contain all fields required for recalculation');
            }
            return deviceState;
        } catch (error) {
            this.showError();
            throw Error(`Project data is corrupt. Cannot prefill the state. ${error}`);
        }
    }

    private projectToHouseholdState(project: ProjectResponseGetById): HouseholdValues | undefined {
        if (!project!.building?.household) {
            return undefined;
        }

        const household: HouseholdValues = {
            isIncomeLowForSubsidy: { value: project!.building?.household?.isIncomeLowForSubsidy! },
            isOwnerOfTheBuilding: { value: project!.building?.household?.isOwnerOfTheBuilding! },
            isOwnerMainResidence: { value: project!.building?.household?.isOwnerMainResidence! }
        };

        if (project.projectType === ProjectType.Renovation) {
            household.isOldHeatingSystemDefective = { value: project!.building?.household?.isOldHeatingSystemDefective! };
        }

        return household;
    }

    private projectToEnergyConsumptionState(project: ProjectResponseGetById): EnergyConsumptionFormValue {
        try {
            const energyConsumption = {
                [FormKeys.RequiredEnergyPerYear]: project!.building.currentHeatingSystem!.consumption!,
                [FormKeys.NonHeatingElectricityPerYear]: project!.building.electricityDemand!
            };

            return energyConsumption;
        } catch (error) {
            this.showError();
            throw Error(`Project data is corrupt. Cannot prefill the state. ${error}`);
        }
    }

    private projectToHeatingDistributionState(project: ProjectResponseGetById): HeatingDistribution {
        try {
            const heatingDistributionState = {
                distributionMethod: project!.building.heatingType as HeatingDistributionMethod,
                heatingFlowTemperature: project!.building.flowTemperature!,
                heatingSchedule: project!.building.consumptionProfileForHeating as ConsumptionProfile
            };

            if (!hasAllPropsDefined(heatingDistributionState)) {
                this.showError();
                throw Error('Project does not contain all fields required for recalculation');
            }
            return heatingDistributionState;
        } catch (error) {
            this.showError();
            throw Error(`Project data is corrupt. Cannot prefill the state. ${error}`);
        }
    }

    private showError(): void {
        const message = this.translateService.instant('COMMON.ERRORS.DATA_CORRUPT');
        const action = this.translateService.instant('COMMON.BUTTONS.GOT_IT');
        this.snackbarService.showInfo(message, {
            duration: 8000,
            action
        });
    }
}
